<template>
  <div>
    <EmailFormModal
      v-if="emailFormModalVisible"
      :callerType="EmailFormCallerType.RfrAssignmentsRequest"
      :emailRecipient="emailFormAssignment?.RcEmail"
      :companyName="emailFormAssignment?.CompanyName"
      :plantName="emailFormAssignment?.PlantName"
      :assignmentId="emailFormAssignment?.assignmentId"
      @closeModal="emailFormModalVisible = false"
      open
    />
    <AssignmentSchedulesModal
      v-if="schedulesModalVisible"
      :ownsIds="selectedAssignments.map((a) => a.ownsId)"
      @closeModal="schedulesModalVisible = false"
      open
    />
    <AssignmentDetailsModal
      v-if="detailsModalVisible && detailsAssignment"
      :assignment="detailsAssignment"
      @closeModal="detailsModalVisible = false"
      open
    />
    <header class="page-header">
      <div class="header-text">
        RFR Assignments
        <div class="help-tooltip">
          <div class="tooltip-content">List of RFR Plant Assignments</div>
        </div>
        <span class="results-label"
          >{{ displayedAssignments.length }} Plant Assignments</span
        >
      </div>
      <div class="header-input-container">
        <div class="header-input">
          <input
            type="text"
            id="SingleBarSearch"
            name="SingleBarSearch"
            placeholder="Search by Plant, Company, Address"
            class="form-control"
            v-model="searchInput"
            @keyup.enter.prevent="filterAssignmentsAsync"
            @keydown.enter.prevent
          />
          <!-- => Prevent enter click from doing any action      -->
          <span class="search-icon" @click.prevent="filterAssignmentsAsync">
            <svg
              fill="#666"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 30 30"
              width="20px"
              height="30px"
            >
              <path
                d="M 13 3 C 7.4889971 3 3 7.4889971 3 13 C 3 18.511003 7.4889971 23 13 23 C 15.396508 23 17.597385 22.148986 19.322266 20.736328 L 25.292969 26.707031 A 1.0001 1.0001 0 1 0 26.707031 25.292969 L 20.736328 19.322266 C 22.148986 17.597385 23 15.396508 23 13 C 23 7.4889971 18.511003 3 13 3 z M 13 5 C 17.430123 5 21 8.5698774 21 13 C 21 17.430123 17.430123 21 13 21 C 8.5698774 21 5 17.430123 5 13 C 5 8.5698774 8.5698774 5 13 5 z"
              />
            </svg>
          </span>
        </div>
        <span @click="clearAllFilters" class="clear-input"
          ><font-awesome-icon icon="filter" /> Click here to clear all filters
          and show all</span
        >
      </div>
    </header>

    <main class="page-container">
      <div class="sidebar">
        <FilterContainer
          class="location-filter"
          :list="uniqueLocations"
          title="Locations"
          @filter-update="recalculateFilterSelections"
          @clear-filter-selections="clearLocationFilter"
        />
        <ToggleSwitch
          v-if="isTeamLeader"
          v-model="showTeamAssignments"
          text="Show My Team Assignments"
        />
      </div>
      <div class="main-content">
        <Loader
          v-if="loading"
          class="assignments-loader"
          size="large"
          type="pulsing"
        />
        <div v-else>
          <MultiSelect
            class="plant-multiselect"
            :items="plants"
            v-model:value="selectedPlants"
            :auto-close="false"
            placeholder="Choose one or more plants"
            itemsName="Plants"
            text-field="name"
            data-item-key="id"
            @close="filterAssignmentsAsync()"
            @tag-clear-click="filterAssignmentsAsync()"
            data-cy="plant-select"
          />
          <section class="no-results" v-if="displayedAssignments.length === 0">
            No results found.<br />
            Please change your filters or search term and try again.
          </section>
          <section class="results" v-else>
            <div class="results-actions">
              <Button
                theme-color="primary"
                class="schedules-button"
                :disabled="selectedAssignments.length === 0"
                @click="schedulesModalVisible = true"
                type="button"
                data-cy="schedules-button"
              >
                {{ getSchedulesButtonText }}
              </Button>
              <Button
                theme-color="secondary"
                :disabled="displayedAssignments.length === 0"
                @click="selectAllAssignments"
                type="button"
                data-cy="select-all-button"
              >
                Select All Plants
              </Button>
              <Button
                theme-color="primary"
                :disabled="selectedAssignments.length === 0"
                @click="clearAssignmentsSelection()"
                type="button"
                data-cy="clear-selection-button"
              >
                Clear Selection
              </Button>
            </div>
            <AssignmentTile
              v-for="a in displayedAssignments"
              :assignment="a"
              :key="a.assignmentId"
              class="result-item"
              :teamAssignments="showTeamAssignments"
              @sendEmailClicked="onSendEmailClicked"
              @detailsClicked="onGroupDetailsClicked"
            />
          </section>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeMount, watch } from "vue";
import { api } from "@/api/";
import { useMainStore } from "@/stores/useMainStore";
import FilterContainer from "@/shared/ui/filter-container/FilterContainer.vue";
import states from "states-us";
import type { FilterListItem } from "@/shared/ui/filter-container/filterListItem";
import "vue-multiselect/dist/vue-multiselect.css";
import AssignmentTile from "./AssignmentTile.vue";
import { Button } from "@progress/kendo-vue-buttons";
import type {
  AssignmentWithSelection,
  GetRfrAssigmentsWithFilteringRequest,
} from "@/api/rfrAssignments";
import AssignmentSchedulesModal from "./AssignmentSchedulesModal.vue";
import MultiSelect from "@/shared/ui/inputs/MultiSelect.vue";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import EmailFormModal from "@/shared/ui/modal/EmailFormModal.vue";
import { EmailFormCallerType } from "@/Domain/EmailFormCallerType";
import type { FilterExpression } from "@/Domain/FilterExpression";
import AssignmentDetailsModal from "./AssignmentDetailsModal.vue";
import { Loader } from "@progress/kendo-vue-indicators";
import ToggleSwitch from "@/shared/ui/switch/ToggleSwitch.vue";
import { CacheService } from "@/shared/services/CacheService";
import { createCacheKey } from "@/shared/helpers/cacheHelper";

const main = useMainStore();
const urlParams = new URLSearchParams(window.location.search);
const searchInput = ref<string>("");

const rfrPlantList = ref<AssignmentWithSelection[]>([]);
const displayedAssignments = ref<AssignmentWithSelection[]>([]);
const schedulesModalVisible = ref<boolean>(false);
const plants = ref<{ id: number; name: string }[]>([]);
const selectedPlants = ref<{ id: number; name: string }[]>([]);
const uniqueLocations = ref<FilterListItem[]>([]);
const skip = ref<number>(0);
const filter = ref<FilterExpression>({} as FilterExpression);
const loading = ref<boolean>(false);
const showTeamAssignments = ref<boolean>(
  urlParams.get("mode")?.toLowerCase() === "team"
);
const isTeamLeader = ref<boolean>(false);

const emailFormModalVisible = ref<boolean>(false);
const emailFormAssignment = ref<AssignmentWithSelection>(
  {} as AssignmentWithSelection
);

const detailsModalVisible = ref<boolean>(false);
const detailsAssignment = ref<AssignmentWithSelection>();

const selectedAssignments = computed(() => {
  return displayedAssignments.value.filter((p) => p.selected);
});

const getSchedulesButtonText = computed(() => {
  const count = selectedAssignments.value.length;
  return `Get Schedules for ${count > 0 ? count + " " : ""}Selected Plant${
    count > 1 ? "s" : ""
  }`;
});

//TODO:
//1. Add/implement filters for location and plant name. and maybe others
//2. Add private and public/shared plant comments
//3. Get Last EIR information
//4. Implement schlepper companies
//5. Implement Link to last EIR etc
//6. Implement main search by multiple values

//Enhancements more than existing functionality
//01. add list of visits (open and submitted) to navigate from this page
//02. build connection to EFR??
//03. Refactor EFR for workflow to include Accounting??

searchInput.value =
  new URLSearchParams(window.location.search).get("search") ?? "";

function clearAllFilters() {
  selectedPlants.value = [...plants.value];
  uniqueLocations.value.forEach((loc) => {
    loc.checked = false;
    loc.children.forEach((child) => (child.checked = false));
  });
  searchInput.value = "";
  filterAssignmentsAsync();
}

// const searchAssignmentsAsync = async function () {
//   await getRfrAssignmentsAsync();
// };

const filterAssignmentsAsync = async function filterAssignmentsAsync() {
  // const filterModel = {
  //   FilterGroups: [],
  //   BooleanLogicOperator: EnumFilterStatementConnector.And,
  // } as FilterExpression;

  // if (
  //   selectedPlants.value.length > 0 &&
  //   selectedPlants.value.length != plants.value.length
  // ) {
  //   selectedPlants.value.forEach((p) => {
  //     filterModel.FilterGroups.push({
  //       FilterFields: [
  //         {
  //           Operator: "eq",
  //           Value: p.id.toString(),
  //         },
  //       ],
  //       BooleanLogicOperator: EnumFilterStatementConnector.Or,
  //       FieldName: "PlantID",
  //       InternalFieldName: "",
  //     });
  //   });
  // };

  // filter.value = filterModel;

  // await getRfrAssignmentsAsync();

  let assignments = rfrPlantList.value?.filter((a) =>
    selectedPlants.value?.find((p) => p.id == a.PlantID)
  );

  if (searchInput.value) {
    assignments = assignments?.filter(
      (a) =>
        a.PlantName.toLowerCase().includes(searchInput.value!.toLowerCase()) ||
        a.PlantAddressToOneLine.toLowerCase().includes(
          searchInput.value!.toLowerCase()
        ) ||
        a.CompanyName.toLowerCase().includes(searchInput.value!.toLowerCase())
    );
  }

  const locs = uniqueLocations.value.filter((l) => l.checked);

  if (locs.find((l) => l.label == "USA")) {
    locs.push({ type: "Location", label: "", children: [], checked: true });
  }

  const childrenLocs = uniqueLocations.value
    .flatMap((loc) => loc.children)
    .filter((c) => c.checked);

  if (locs.length > 0 || childrenLocs.length > 0) {
    assignments = assignments?.filter((a) => {
      const state = states.find((x) => x.abbreviation == a.PlantState)?.name;
      return (
        locs.find((l) => l.label == a.PlantCountry) ||
        childrenLocs.find(
          (cl) =>
            cl.label == state ||
            cl.label == a.PlantCity ||
            cl.label == a.PlantState
        )
      );
    });
  }

  displayedAssignments.value = assignments || [];
};

function setupPlantLocationFilters() {
  uniqueLocations.value = []; //reset

  const locationCountries = ref<string[]>([]);
  rfrPlantList.value?.forEach((a) => {
    if (!locationCountries.value?.includes(a.PlantCountry)) {
      if (a.PlantCountry == "" && !locationCountries.value.includes("USA")) {
        locationCountries.value.push("USA");
      } else if (a.PlantCountry != "") {
        locationCountries.value.push(a.PlantCountry);
      }
    }
  });

  // sort alphabetically by country but push USA to the top of the list
  locationCountries.value.sort((a, b) => (a < b ? -1 : 1));

  // add cities etc
  for (const location of locationCountries.value) {
    const locationChildren = [] as { label: string; checked: boolean }[]; //reset on each run
    const unsortedChildList = rfrPlantList.value;

    unsortedChildList?.forEach((loc) => {
      if (
        loc.PlantCountry == location ||
        (loc.PlantCountry == "" && location == "USA")
      ) {
        let locationChild = "";

        if (loc.PlantState.length > 0) locationChild = loc.PlantState;
        else if (loc.PlantCity.length > 0) locationChild = loc.PlantCity;
        else locationChild = "";

        // replace state codes with state names
        if (loc.PlantCountry == "USA" || loc.PlantCountry == "") {
          locationChild =
            states.find((x) => x.abbreviation == locationChild)?.name ??
            locationChild;
        }

        if (!locationChildren.find((lc) => lc.label == locationChild)) {
          locationChildren.push({
            label: locationChild,
            checked: false,
          });
        }
      }
    });

    locationChildren.sort((a, b) => (a.label < b.label ? -1 : 1));

    uniqueLocations.value.push({
      type: "Location",
      label: location,
      children: locationChildren,
      checked: false,
    });
  }
}

const onSendEmailClicked = function (assignment: AssignmentWithSelection) {
  emailFormAssignment.value = assignment;
  emailFormModalVisible.value = true;
};

const onGroupDetailsClicked = function (assignment: AssignmentWithSelection) {
  detailsAssignment.value = assignment;
  detailsModalVisible.value = true;
};

const getRfrAssignmentsAsync = async function () {
  if (loading.value) return;

  loading.value = true;

  const requestParams = {
    userLogin: main.userLogin,
    searchValue: "",
    onlyMainCompanies: true,
    filters: "",
    skip: 0,
    take: 99999,
    sortField: "",
    ascending: false,
    valuesForFieldOnly: "",
    teamLeaderMode: showTeamAssignments.value,
  } as GetRfrAssigmentsWithFilteringRequest;

  await handleResponsePromiseAsync(
    api.get("GetRfrAssigmentsWithFiltering", requestParams),
    (data) => {
      rfrPlantList.value = data.ListItems;

      plants.value = rfrPlantList.value
        .map((p) => ({
          id: p.PlantID,
          name: p.PlantName,
        }))
        .filter((p, i, a) => a.findIndex((x) => x.id == p.id) == i);

      displayedAssignments.value = rfrPlantList.value;
      selectedPlants.value = [...plants.value];

      setupPlantLocationFilters();
    },
    "RfrAssignmentsMobileView",
    "getRfrAssignmentsAsync"
  );

  loading.value = false;
};

function clearAssignmentsSelection() {
  displayedAssignments.value.forEach((p) => (p.selected = false));
}

function selectAllAssignments() {
  displayedAssignments.value.forEach((p) => (p.selected = true));
}

function selectOrDeselectChildFilters(value: string) {
  // if a parent is checked always check all the children
  // if a parent is NOT checked, only uncheck if that is the parent that was clicked
  uniqueLocations.value.forEach((loc) =>
    loc.children.forEach((child) => {
      if (loc.checked) {
        child.checked = true;
      } else {
        if (loc.label == value) child.checked = false;
      }
    })
  ); // update all children to parent checked status
}

function recalculateFilterSelections(value: string, filterTier: string) {
  if (filterTier == "parent") {
    selectOrDeselectChildFilters(value);
  }

  filterAssignmentsAsync();
}

function clearLocationFilter() {
  uniqueLocations.value.forEach((loc) => {
    loc.checked = false;
    loc.children.forEach((child) => (child.checked = false));
  });
  filterAssignmentsAsync();
}

const updateIsTeamLeaderValueAsync = async function () {
  const params = {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
  };
  await handleResponsePromiseAsync(
    CacheService.getResponseAsync(
      createCacheKey(params, "IsRfrTeamLeader"),
      () => api.get("IsRfrTeamLeader", params)
    ),
    (data) => {
      isTeamLeader.value = data;
      if (!data) showTeamAssignments.value = false;
    },
    "AssignmentsTable.vue",
    "updateIsTeamLeaderValueAsync"
  );
};

watch(
  () => showTeamAssignments.value,
  () => {
    getRfrAssignmentsAsync();
    urlParams.set("mode", showTeamAssignments.value ? "team" : "my");
    window.history.pushState(
      {},
      "",
      `${window.location.pathname}?${urlParams}`
    );
  }
);

onBeforeMount(async () => {
  await updateIsTeamLeaderValueAsync();
  getRfrAssignmentsAsync();
});
</script>

<style lang="scss">
.switch-wrapper {
  label {
    font-size: 14px;
  }
}

.plant-multiselect {
  margin-bottom: 36px !important;
}
</style>

<style scoped lang="scss">
header.page-header {
  background: #eef4f6;
  padding: 30px 30px 20px;
  margin: 0;
  border: none;
  @media only screen and (min-width: 740px) {
    display: flex;
  }
}

.header-input-container {
  width: 100%;
}

.contact-text {
  font-size: 12px;
  font-weight: normal;
  color: #3c3a3a;
  margin-left: 2rem;
  padding: 15px 30px;
  background: #e1edf1;
  margin: 0px 0px 0px 0px;
}

.header-text {
  color: #3a5da6;
  font-size: 22px;
  font-weight: bold;
  margin-right: 40px;
  .results-label {
    font-size: 14px;
    display: block;
    font-weight: normal;
    color: #444;
  }
}
.header-input {
  flex: 1;
  max-width: 800px;
  position: relative;
  .search-icon {
    cursor: pointer;
    position: absolute;
    top: 10px;
    right: 10px;
  }

  input {
    width: 100%;
    height: 50px;
    box-shadow: 0 0px 10px rgba(0, 0, 0, 0.05);
    &:focus {
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
      border-color: #3a5da6;
    }
  }
}
.clear-input {
  display: block;
  margin-top: 10px;
  cursor: pointer;
  color: #666;
  text-decoration: underline;
}
.page-container {
  padding: 30px 12px 60px;
  background: white;
  min-height: 100%;
  @media only screen and (min-width: 740px) {
    display: flex;
  }
  @include media-breakpoint-up(sm) {
    padding: 30px 60px 60px;
  }
  .sidebar {
    width: 100%;
    flex: 1;
    margin: 0;
    padding: 0;
    margin-right: 40px;
    display: flex;
    flex-direction: column;
    margin-bottom: 32px;
  }
  .main-content {
    flex: 3;
  }
}

.assignments-loader {
  display: flex;
  justify-content: center;
}

.results-actions {
  display: flex;
  margin-bottom: 20px;
  gap: 16px;
  flex-wrap: wrap;

  .schedules-button {
    flex-basis: 100%;

    @include media-breakpoint-up(sm) {
      flex-basis: auto;
    }
  }

  button {
    flex-grow: 1;

    @include media-breakpoint-up(sm) {
      flex-grow: 0;
    }
  }
}

.more-results {
  text-align: right;
  padding-top: 20px;
  border-top: 3px solid #efefef;
  a {
    cursor: pointer;
    color: #3a5da6;
    display: inline-block;
    padding: 8px 30px;
    box-shadow: 0 0 15px rgb(0 0 0 / 10%);
    font-weight: bold;
    font-size: 18px;
    &:hover {
      background: #efefef;
    }
  }
}
section.no-results {
  text-align: center;
  padding-top: 40px;
  color: #666;
}

/* tooltip */
.help-tooltip {
  position: relative;
  display: inline-block;
  z-index: 10;
}
.fa-question-circle {
  font-size: 16px;
  display: block;
  line-height: 30px;
  color: #399cd3;
  cursor: pointer;
}
.fa-question-circle:hover ~ .tooltip-content {
  display: block;
}
.tooltip-content {
  display: none;
  background: #ffffe8;
  position: absolute;
  left: 20px;
  top: 3px;
  padding: 10px 15px;
  width: 300px;
  border: 1px solid #e4e4d5;
  border-radius: 4px;
  box-shadow: 1px 1px 10px rgb(0 0 0 / 10%);
  font-size: 14px;
  font-weight: normal;
  color: #222;
}
.legalese {
  flex: 1;
  text-align: left;
  color: #999;
  padding: 10px 40px;
  border-top: 1px solid #eee;
  font-size: 12px;
}
.commentButton {
  flex: 1;
  text-align: left;
  color: #999;
  height: 25px;
  margin: 10px;
}
.labelname {
  //display: inline-block;
  font-size: 12px;
  color: #999;
  font-weight: bold;
}
</style>
